import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import {
  fetchSubscriptionDetails as fetchSubscriptionDetailsApi,
  pauseSubscription as pauseSubscriptionApi,
  resumeSubscription as resumeSubscriptionApi,
  cancelSubscription as cancelSubscriptionApi,
} from '../../util/api';
import { fetchCurrentUser } from '../../ducks/user.duck';

// ================ Async Thunks ================ //

export const fetchSubscriptionDetailsThunk = createAsyncThunk(
  'SubscriptionSettingsPage/fetchSubscriptionDetails',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await fetchSubscriptionDetailsApi({ userId });
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const pauseSubscriptionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/pauseSubscription',
  async ({ userId, subscriptionId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await pauseSubscriptionApi({ userId, subscriptionId });
      // Refresh current user and subscription details
      dispatch(fetchCurrentUser());
      dispatch(fetchSubscriptionDetailsThunk(userId));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const resumeSubscriptionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/resumeSubscription',
  async ({ userId, subscriptionId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await resumeSubscriptionApi({ userId, subscriptionId });
      // Refresh current user and subscription details
      dispatch(fetchCurrentUser());
      dispatch(fetchSubscriptionDetailsThunk(userId));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const cancelSubscriptionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/cancelSubscription',
  async ({ userId, subscriptionId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await cancelSubscriptionApi({ userId, subscriptionId });
      // Refresh current user and subscription details
      dispatch(fetchCurrentUser());
      dispatch(fetchSubscriptionDetailsThunk(userId));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

// Backward compatible action wrappers
export const fetchSubscriptionDetails = userId => dispatch => {
  return dispatch(fetchSubscriptionDetailsThunk(userId));
};

export const pauseSubscription = payload => dispatch => {
  return dispatch(pauseSubscriptionThunk(payload));
};

export const resumeSubscription = payload => dispatch => {
  return dispatch(resumeSubscriptionThunk(payload));
};

export const cancelSubscription = payload => dispatch => {
  return dispatch(cancelSubscriptionThunk(payload));
};

// ================ Slice ================ //

const subscriptionSettingsPageSlice = createSlice({
  name: 'SubscriptionSettingsPage',
  initialState: {
    subscriptionDetails: null,
    fetchSubscriptionDetailsInProgress: false,
    fetchSubscriptionDetailsError: null,
    pauseInProgress: false,
    pauseError: null,
    resumeInProgress: false,
    resumeError: null,
    cancelInProgress: false,
    cancelError: null,
    successMessage: null,
  },
  reducers: {
    clearMessages: state => {
      state.pauseError = null;
      state.resumeError = null;
      state.cancelError = null;
      state.successMessage = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch details
      .addCase(fetchSubscriptionDetailsThunk.pending, state => {
        state.fetchSubscriptionDetailsInProgress = true;
        state.fetchSubscriptionDetailsError = null;
      })
      .addCase(fetchSubscriptionDetailsThunk.fulfilled, (state, action) => {
        state.fetchSubscriptionDetailsInProgress = false;
        state.subscriptionDetails = action.payload;
      })
      .addCase(fetchSubscriptionDetailsThunk.rejected, (state, action) => {
        state.fetchSubscriptionDetailsInProgress = false;
        state.fetchSubscriptionDetailsError = action.payload;
      })
      // Pause
      .addCase(pauseSubscriptionThunk.pending, state => {
        state.pauseInProgress = true;
        state.pauseError = null;
        state.successMessage = null;
      })
      .addCase(pauseSubscriptionThunk.fulfilled, (state, action) => {
        state.pauseInProgress = false;
        state.successMessage = action.payload?.message || 'Subscription paused successfully.';
      })
      .addCase(pauseSubscriptionThunk.rejected, (state, action) => {
        state.pauseInProgress = false;
        state.pauseError = action.payload;
      })
      // Resume
      .addCase(resumeSubscriptionThunk.pending, state => {
        state.resumeInProgress = true;
        state.resumeError = null;
        state.successMessage = null;
      })
      .addCase(resumeSubscriptionThunk.fulfilled, (state, action) => {
        state.resumeInProgress = false;
        state.successMessage = action.payload?.message || 'Subscription resumed successfully.';
      })
      .addCase(resumeSubscriptionThunk.rejected, (state, action) => {
        state.resumeInProgress = false;
        state.resumeError = action.payload;
      })
      // Cancel
      .addCase(cancelSubscriptionThunk.pending, state => {
        state.cancelInProgress = true;
        state.cancelError = null;
        state.successMessage = null;
      })
      .addCase(cancelSubscriptionThunk.fulfilled, (state, action) => {
        state.cancelInProgress = false;
        state.successMessage = action.payload?.message || 'Subscription cancelled successfully.';
      })
      .addCase(cancelSubscriptionThunk.rejected, (state, action) => {
        state.cancelInProgress = false;
        state.cancelError = action.payload;
      });
  },
});

export const { clearMessages } = subscriptionSettingsPageSlice.actions;

export const loadData = (params, search) => (dispatch, getState) => {
  const { currentUser } = getState().user;
  const userId = currentUser?.id?.uuid;
  if (userId) {
    return dispatch(fetchSubscriptionDetailsThunk(userId));
  }
  return Promise.resolve();
};

export default subscriptionSettingsPageSlice.reducer;
